import React, { lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import Login from "./features/login/pages/Login";
import Layout from "./layout/Layout";
import Guard from "./routes/Guard";
import { startTokenTimer, setupCrossTabLogout } from "./Services/tokenUtils";
import ProtectedRoute from "./routes/ProtectedRoute";
import PublicRoute from "./routes/PublicRoute";

/**
 * Router-based App shell.
 *
 * Each module gets a real URL. Browser back/forward works, deep links work,
 * Ctrl+Click on a sidebar item opens that module in a new browser tab.
 *
 * Permission gating happens via <Guard p="...">, which is defense-in-depth on
 * top of the sidebar's permission filter (the API also enforces the same
 * permission server-side; that remains the source of truth).
 */

const Dashboard         = lazy(() => import("./features/Dashboard/pages/Dashboard"));
const Company           = lazy(() => import("./features/company/pages/Company"));
const Stores            = lazy(() => import("./features/stores/pages/Stores"));
const Vendors           = lazy(() => import("./features/vendors/pages/Vendors"));
const Customers         = lazy(() => import("./features/customers/pages/Customers"));
const Categories        = lazy(() => import("./features/categories/pages/Categories"));
const Products          = lazy(() => import("./features/Products/pages/Products"));
const ProductGallery    = lazy(() => import("./features/Products/pages/ProductGallery"));
const Inventory         = lazy(() => import("./features/Inventory/pages/Inventory"));
const InventoryTracking = lazy(() => import("./features/InventoryTracking/pages/InventoryTracking"));
const StockIn           = lazy(() => import("./features/StockIn/pages/StockIn"));
const StockOut          = lazy(() => import("./features/StockOut/pages/StockOut"));
const Purchase          = lazy(() => import("./features/Purchase/pages/Purchase"));
const MaterialRequirement = lazy(() => import("./features/MaterialRequirement/pages/MaterialRequirement"));
const Feeder            = lazy(() => import("./features/feeder/pages/feeder"));
const Bom               = lazy(() => import("./features/Bom/pages/Bom"));
const Job               = lazy(() => import("./features/Job/pages/Job"));
const Orders            = lazy(() => import("./features/Order/pages/Orders"));
const UserManagement    = lazy(() => import("./features/UserManagement/pages/UserManagement"));
const UserRole          = lazy(() => import("./features/UserRoles/pages/UserRole"));
const Audit             = lazy(() => import("./features/audit/pages/Audit"));
const ChangePassword    = lazy(() => import("./features/ChangePassword/pages/ChangePassword"));

export default function App() {
  useEffect(() => {
    startTokenTimer();
    const cleanup = setupCrossTabLogout();
    return cleanup;
  }, []);

  return (
    <>
      <Toaster position="top-center" />

      <BrowserRouter>
        <Routes>
          {/* Public — already authenticated → bounce to /dashboard via PublicRoute. */}
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />

          {/* Protected layout: every authenticated path renders inside Layout's <Outlet/>. */}
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            <Route path="/dashboard"          element={<Dashboard />} />
            <Route path="/company"            element={<Guard p="companyAccess"><Company /></Guard>} />
            <Route path="/stores"             element={<Guard p="storesAccess"><Stores /></Guard>} />
            <Route path="/vendors"            element={<Guard p="vendorsAccess"><Vendors /></Guard>} />
            <Route path="/customers"          element={<Guard p="customerAccess"><Customers /></Guard>} />
            <Route path="/categories"         element={<Guard p="categoriesAccess"><Categories /></Guard>} />
            <Route path="/products"           element={<Guard p="productsAccess"><Products /></Guard>} />
            <Route path="/gallery"            element={<Guard p="productsAccess"><ProductGallery /></Guard>} />
            <Route path="/inventory"          element={<Guard p="materialAccess"><Inventory /></Guard>} />
            <Route path="/inventory-tracking" element={<Guard p="inventoryAccess"><InventoryTracking /></Guard>} />
            <Route path="/stock-in"           element={<Guard p="stockAccess"><StockIn /></Guard>} />
            <Route path="/stock-out"          element={<Guard p="stockOutAccess"><StockOut /></Guard>} />
            <Route path="/purchase"           element={<Guard p="purchaseAccess"><Purchase /></Guard>} />
            <Route path="/material-requirement" element={<Guard p="materialRequirementAccess"><MaterialRequirement /></Guard>} />
            <Route path="/feeder"             element={<Guard p="feederAccess"><Feeder /></Guard>} />
            <Route path="/bom"                element={<Guard p="bomAccess"><Bom /></Guard>} />
            <Route path="/jobs"               element={<Guard p="jobManagementAccess"><Job /></Guard>} />
            <Route path="/orders"             element={<Guard p="orderAccess"><Orders /></Guard>} />
            <Route path="/user-management"    element={<Guard p="userManagementAccess"><UserManagement /></Guard>} />
            <Route path="/user-roles"         element={<Guard p="userRolesAccess"><UserRole /></Guard>} />
            <Route path="/audit"              element={<Guard p="auditAccess"><Audit /></Guard>} />
            <Route path="/change-password"    element={<ChangePassword />} />

            {/* Unknown protected path → dashboard. */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </>
  );
}
